/* eslint-disable @typescript-eslint/no-use-before-define */
import { createContext, useContext, useState } from 'react'
import type { PropsWithChildren } from 'react'

import { useDeleteProductsMutation } from '@Store/api/product'
import type { Product } from '@Store/types/product'

import {
  BaseModal,
  type BaseModalDispatch,
  BaseModalProvider,
  type BaseModalState,
  useBaseModalDispatch,
  useBaseModalState,
} from './BaseModal'

interface DeleteProductState extends BaseModalState {
  product?: Product
}

interface DeleteProductDispatch extends BaseModalDispatch {
  setProduct: (payload: Product) => void
  deleteProduct: (payload?: any) => Promise<any>
  resetDeleteProduct: () => void
}

const DeleteProductStateContext = createContext<DeleteProductState>(
  {} as DeleteProductState,
)
DeleteProductStateContext.displayName = 'DeleteProductState'

const DeleteProductDispatchContext = createContext<DeleteProductDispatch>(
  {} as DeleteProductDispatch,
)
DeleteProductDispatchContext.displayName = 'DeleteProductDispatch'

function DeleteProductProvider({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  return (
    <BaseModalProvider>
      <DeleteProductProviderInner modalContent={modalContent}>
        {children}
      </DeleteProductProviderInner>
    </BaseModalProvider>
  )
}

function DeleteProductProviderInner({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  const [product, setProduct] = useState<Product>()

  const baseState = useBaseModalState()
  const baseDispatch = useBaseModalDispatch()

  const [deleteProduct, { reset: resetDeleteProduct }] =
    useDeleteProductsMutation()

  return (
    <DeleteProductStateContext.Provider value={{ ...baseState, product }}>
      <DeleteProductDispatchContext.Provider
        value={{
          ...baseDispatch,
          setProduct,
          deleteProduct,
          resetDeleteProduct,
        }}
      >
        <BaseModal isOpen={baseState.isOpen} close={baseDispatch.close}>
          {modalContent}
        </BaseModal>
        {children}
      </DeleteProductDispatchContext.Provider>
    </DeleteProductStateContext.Provider>
  )
}

function useDeleteProductState(): DeleteProductState {
  try {
    const context = useContext(DeleteProductStateContext)
    return context
  } catch (error) {
    throw new Error(
      'useDeleteProductState must be used within a DeleteProductProvider',
      { cause: error },
    )
  }
}

function useDeleteProductDispatch(): DeleteProductDispatch {
  try {
    const context = useContext(DeleteProductDispatchContext)
    return context
  } catch (error) {
    throw new Error(
      'useDeleteProductDispatch must be used within a DeleteProductProvider',
      { cause: error },
    )
  }
}

export {
  DeleteProductProvider,
  useDeleteProductState,
  useDeleteProductDispatch,
}
